import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import Badge from 'Components/Atoms/Badge/Badge';
import Button from 'Components/Atoms/Button/Button';
import OutlineTrash from 'Components/Atoms/OutlineIcons/OutlineTrash';
import DeleteConfirmDialog from 'Components/Molecules/DeleteConfirmDialog/DeleteConfirmDialog';
import { useSnackbar } from 'Components/Molecules/SnackbarProvider/SnackbarProvider';
import { deleteContactsImportContacts } from 'Services/invoices';
import { formatLongDate } from 'Utils/formatters';
import useLocale from 'Hooks/useLocale';
import { trackError } from 'Utils/errorMonitoring';

export default function ContactsImportsHistoryItem(props) {
    const { contactsImport } = props;
    const {
        id,
        file_name_original,
        status,
        items,
        items_created,
        items_updated,
        is_contacts_deleted,
        contacts_deleted_date,
        creation_date,
    } = contactsImport;

    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const locale = useLocale();
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation('contacts');

    const isImported = status === 'OK';

    const statusText = useMemo(() => {
        switch (status) {
            case 'OK':
                return t('imports-history-status-ok');
            case 'ERROR':
                return t('imports-history-status-error');
            default:
                return t('imports-history-status-processing');
        }
    }, [status, t]);

    const statusColor = useMemo(() => {
        switch (status) {
            case 'OK':
                return 'success';
            case 'ERROR':
                return 'danger';
            default:
                return 'warning';
        }
    }, [status]);

    const handleDelete = async () => {
        setIsDeleting(true);

        try {
            await deleteContactsImportContacts(id);
            await queryClient.invalidateQueries({ queryKey: ['contacts-imports'] });
            await queryClient.invalidateQueries({ queryKey: ['contacts'] });
        } catch (error) {
            trackError(error);
            enqueueSnackbar(t('imports-history-delete-error'), { variant: 'error' });
        } finally {
            setIsDeleting(false);
            setConfirmingDelete(false);
        }
    };

    return (
        <div className="mb-4.5 border-b border-gray-500/10 pb-4.5">
            <div className="mb-4 flex items-center gap-2">
                <div className="min-w-0 grow">
                    <div className="text-base font-semibold text-gray-800">
                        {formatLongDate(creation_date, { locale })}
                    </div>
                    {file_name_original && <div className="truncate text-xs text-gray-500">{file_name_original}</div>}
                </div>
                <Badge color={statusColor}>{statusText}</Badge>
            </div>

            {isImported && (
                <div className="grid gap-1.5">
                    <div className="flex items-center justify-between text-sm text-gray-500">
                        <div>{t('imports-history-imported')}:</div>
                        <div>{items_created ?? 0}</div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                        <div>{t('imports-history-updated')}:</div>
                        <div>{items_updated ?? 0}</div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                        <div>{t('imports-history-total')}:</div>
                        <div>{items ?? 0}</div>
                    </div>
                </div>
            )}

            {isImported && (
                <div className="pt-4">
                    {is_contacts_deleted ? (
                        <div className="text-xs text-gray-500">
                            {t('imports-history-deleted', {
                                date: formatLongDate(contacts_deleted_date, { locale }),
                                interpolation: { escapeValue: false },
                            })}
                        </div>
                    ) : (
                        <Button size="small" isBusy={isDeleting} onClick={() => setConfirmingDelete(true)}>
                            <OutlineTrash size={18} />
                            {t('imports-history-delete')}
                        </Button>
                    )}
                </div>
            )}

            <DeleteConfirmDialog
                open={confirmingDelete}
                onClose={() => setConfirmingDelete(false)}
                onSubmit={handleDelete}
                title={t('imports-history-delete-title')}
                description={t('imports-history-delete-description')}
            />
        </div>
    );
}

ContactsImportsHistoryItem.propTypes = {
    contactsImport: PropTypes.shape({
        id: PropTypes.string.isRequired,
        file_name_original: PropTypes.string,
        status: PropTypes.string,
        items: PropTypes.number,
        items_created: PropTypes.number,
        items_updated: PropTypes.number,
        is_contacts_deleted: PropTypes.bool,
        contacts_deleted_date: PropTypes.instanceOf(Date),
        creation_date: PropTypes.instanceOf(Date),
    }).isRequired,
};
